import { useEffect, useMemo, useRef, useState } from "react";
import "@/App.css";
import { BrowserRouter, Link, Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import Lenis from "lenis";
import {
  AirplaneTakeoff,
  CheckCircle,
  GlobeHemisphereWest,
  Headset,
  InstagramLogo,
  SealCheck,
  WhatsappLogo } from
"@phosphor-icons/react";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useInView } from "react-intersection-observer";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Toaster, toast } from "@/components/ui/sonner";
import { destinations, liveDealsData, packagesData, testimonials, whyChooseItems } from "@/data/travelData";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const enquirySchema = z.object({
  name: z.string().min(2, "Please enter your full name"),
  phone: z.string().min(7, "Please enter a valid phone number"),
  email: z.string().email("Please enter a valid email"),
  destination: z.string().min(2, "Please add your preferred destination"),
  travel_dates: z.string().min(3, "Please add your travel dates")
});

const featureIcons = {
  "Curated Travel Experiences": GlobeHemisphereWest,
  "Handpicked Hotels": SealCheck,
  "24/7 Travel Support": Headset,
  "Best Price Guarantee": CheckCircle
};

const heroImage =
"https://images.unsplash.com/photo-1766910701111-9eee02328e95?auto=format&fit=crop&w=2200&q=80";
const aboutImage =
"https://images.unsplash.com/photo-1625244724120-1fd1d34d00f6?auto=format&fit=crop&w=1400&q=80";
const brandLogo = "/mkot-logo.png";

const navItems = [
{ label: "Destinations", id: "destinations" },
{ label: "Packages", id: "packages" },
{ label: "Visa", id: "visa" },
{ label: "Flights", id: "flights" },
{ label: "Why Us", id: "why-us" },
{ label: "Contact", id: "contact" }];


const visaAssistance = [
{
  title: "Tourist Visa Assistance",
  description: "Fast documentation support, checklist guidance, and timely filing for leisure travel."
},
{
  title: "Business Visa Support",
  description: "Priority consultation for business travellers with document verification and updates."
},
{
  title: "Family Visit Visa Help",
  description: "End-to-end support for family visit applications with smooth process coordination."
}];


const scrollToSection = (id) => {
  const section = document.getElementById(id);
  if (section) {
    section.scrollIntoView({ behavior: "smooth", block: "start" });
  }
};

const buildDealWhatsappLink = (dealTitle) =>
`https://wa.me/918448418990?text=${encodeURIComponent(`Hi My Kind of Trip, I want details for ${dealTitle}.`)}`;

const RevealBlock = ({ children, delay = 0, className = "", testId }) => {
  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.16 });

  return (
    <div
      ref={ref}
      className={className}
      data-testid={testId}
      style={{
        opacity: inView ? 1 : 0,
        transform: inView ? "translateY(0px) scale(1)" : "translateY(42px) scale(0.96)",
        filter: inView ? "blur(0px)" : "blur(4px)",
        transition: `all 1050ms cubic-bezier(0.22, 1, 0.36, 1) ${delay}ms`
      }}>

      {children}
    </div>);

};

const Home = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [authUser, setAuthUser] = useState(null);

  const whatsappLink = useMemo(
    () =>
    "https://wa.me/918448418990?text=Hi%20My%20Kind%20of%20Trip%2C%20I%20want%20to%20plan%20a%20customized%20holiday.",
    []
  );

  const instagramLink = useMemo(() => "https://www.instagram.com/mykindoftripofficial?srtk=eXlvYm5tZTY0cmlx", []);

  const form = useForm({
    resolver: zodResolver(enquirySchema),
    defaultValues: {
      name: "",
      phone: "",
      email: "",
      destination: "",
      travel_dates: ""
    }
  });

  const submitEnquiry = async (values) => {
    setIsSubmitting(true);
    try {
      const response = await axios.post(`${API}/enquiries`, values);
      toast.success(response.data?.message || "Thanks! Your enquiry was sent successfully.");
      form.reset();
    } catch (error) {
      console.error(error);
      const errorMessage = error?.response?.data?.detail || "We couldn't submit your enquiry. Please try again.";
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    document.title = "My Kind of Trip | Premium Travel Agency";

    const initApi = async () => {
      try {
        await axios.get(`${API}/`);
        const authResponse = await axios.get(`${API}/auth/me`, { withCredentials: true });
        setAuthUser(authResponse.data);
      } catch (error) {
        setAuthUser(null);
      }
    };
    initApi();

    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    let frameId;

    const raf = (time) => {
      lenis.raf(time);
      frameId = requestAnimationFrame(raf);
    };

    frameId = requestAnimationFrame(raf);

    return () => {
      cancelAnimationFrame(frameId);
      lenis.destroy();
    };
  }, []);

  return (
    <div className="relative overflow-x-hidden bg-[#110f0b] text-[#f7f2e8]" data-testid="homepage-wrapper">
      <div className="luxury-noise" />
      <div className="ultra-orb ultra-orb-top" />
      <div className="ultra-orb ultra-orb-mid" />

      <nav className="glass-nav" data-testid="top-navigation">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 sm:px-8">
          <button
            type="button"
            className="rounded-md transition-transform duration-300 hover:scale-[1.03]"
            onClick={() => scrollToSection("hero")}
            data-testid="brand-logo-button">

            <img src={brandLogo} alt="My Kind of Trip" className="h-20 w-auto object-contain sm:h-24" data-testid="brand-logo-image" />
          </button>

          <div className="hidden items-center gap-6 md:flex">
            {navItems.map((item, index) =>
            <button
              key={item.id}
              type="button"
              className="text-sm font-semibold tracking-[0.13em] text-[#2b2214] transition-colors duration-300 hover:text-[#8d6415]"
              onClick={() => scrollToSection(item.id)}
              data-testid={`nav-link-${item.id}-${index}`}>

                {item.label}
              </button>
            )}

            <a
              href={instagramLink}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 text-sm font-semibold tracking-[0.1em] text-[#2b2214] transition-colors duration-300 hover:text-[#8d6415]"
              data-testid="header-instagram-link">

              <InstagramLogo size={18} weight="fill" data-testid="header-instagram-icon" />
              Instagram
            </a>
          </div>

          <div className="flex items-center gap-3">
            <Link
              to={authUser ? "/dashboard" : "/login"}
              className="gold-cta-outline-btn hidden md:inline-flex"
              data-testid="nav-google-signin-button"
            >
              {authUser ? "Dashboard" : "Google Sign-In"}
            </Link>
            <Button
              type="button"
              onClick={() => scrollToSection("contact")}
              className="gold-cta-btn hidden md:inline-flex"
              data-testid="nav-plan-trip-button">

              Plan Trip
            </Button>
          </div>
        </div>
      </nav>

      <section
        id="hero"
        className="relative flex min-h-[95vh] items-end px-4 pb-16 pt-24 sm:px-8"
        style={{
          backgroundImage: `linear-gradient(118deg, rgba(0,0,0,0.82), rgba(0,0,0,0.28)), radial-gradient(circle at 80% 20%, rgba(212,175,55,0.38), transparent 40%), url(${heroImage})`,
          backgroundSize: "cover",
          backgroundPosition: "center"
        }}
        data-testid="hero-section">

        <div className="mx-auto w-full max-w-7xl">
          <RevealBlock className="max-w-3xl space-y-7" delay={80} testId="hero-content-wrap">
            <p className="font-body text-sm uppercase tracking-[0.36em] text-[#f7d98d]" data-testid="hero-kicker-text">
              Ultra Luxury International Holidays
            </p>
            <h1 className="font-display text-4xl font-semibold leading-[1.06] text-white sm:text-5xl lg:text-6xl" data-testid="hero-main-heading">
              Travel the World, Your Way
            </h1>
            <p className="max-w-2xl font-body text-base text-[#f6edd7] md:text-lg" data-testid="hero-subtext">
              Discover curated international holidays designed for your kind of trip.
            </p>
            <div className="flex flex-wrap gap-4" data-testid="hero-cta-group">
              <Button
                type="button"
                className="gold-cta-btn"
                onClick={() => scrollToSection("packages")}
                data-testid="hero-explore-packages-button">

                Explore Packages
              </Button>
              <Button
                type="button"
                variant="outline"
                className="gold-cta-outline-btn"
                onClick={() => scrollToSection("contact")}
                data-testid="hero-plan-your-trip-button">

                Plan Your Trip
              </Button>
            </div>
          </RevealBlock>
        </div>
      </section>

      <section id="destinations" className="mx-auto max-w-7xl px-4 py-20 sm:px-8 lg:py-28" data-testid="destinations-section">
        <RevealBlock className="mb-11 space-y-4" delay={60} testId="destinations-header-wrap">
          <p className="font-body text-sm uppercase tracking-[0.24em] text-[#f2c562]" data-testid="destinations-kicker">
            Popular Destinations
          </p>
          <h2 className="font-display text-4xl font-semibold text-[#fff8eb] sm:text-5xl" data-testid="destinations-heading">

          </h2>
        </RevealBlock>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3" data-testid="destinations-grid">
          {destinations.map((destination, index) =>
          <RevealBlock key={destination.name} delay={110 + index * 70}>
              <Card
              className="glass-lux-card group overflow-hidden border border-[#f3c75d40]"
              data-testid={`destination-card-${index}`}>

                <div className="relative aspect-[4/3] overflow-hidden" data-testid={`destination-image-wrap-${index}`}>
                  <img
                  src={destination.image}
                  alt={destination.name}
                  className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
                  data-testid={`destination-image-${index}`} />

                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/15 to-transparent" />
                </div>

                <CardContent className="space-y-4 p-6">
                  <h3 className="font-display text-3xl text-[#fff5df]" data-testid={`destination-name-${index}`}>
                    {destination.name}
                  </h3>
                  <p className="font-body text-sm text-[#e3d7bf]" data-testid={`destination-description-${index}`}>
                    {destination.description}
                  </p>
                  <Button
                  type="button"
                  className="gold-pill-small"
                  onClick={() => scrollToSection("contact")}
                  data-testid={`destination-view-packages-button-${index}`}>

                    View Packages
                  </Button>
                </CardContent>
              </Card>
            </RevealBlock>
          )}
        </div>
      </section>

      <section id="packages" className="premium-panel px-4 py-20 sm:px-8 lg:py-28" data-testid="packages-section">
        <div className="mx-auto max-w-7xl">
          <RevealBlock className="mb-11 space-y-4" delay={60} testId="packages-header-wrap">
            <p className="font-body text-sm uppercase tracking-[0.24em] text-[#7e5a1a]" data-testid="packages-kicker">
              Featured Packages
            </p>
            <h2 className="font-display text-4xl font-semibold text-[#17120b] sm:text-5xl" data-testid="packages-heading">
              Statement Journeys for Bold Travellers
            </h2>
          </RevealBlock>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-2" data-testid="packages-grid">
            {packagesData.map((pkg, index) =>
            <RevealBlock key={pkg.title} delay={110 + index * 80}>
                <Card className="ultra-light-card overflow-hidden" data-testid={`package-card-${index}`}>
                  <div className="aspect-[4/3] overflow-hidden" data-testid={`package-image-wrap-${index}`}>
                    <img
                    src={pkg.image}
                    alt={pkg.title}
                    className="h-full w-full object-cover transition-transform duration-700 hover:scale-110"
                    data-testid={`package-image-${index}`} />

                  </div>
                  <CardContent className="space-y-4 p-6">
                    <h3 className="font-display text-3xl text-[#1f1a11]" data-testid={`package-title-${index}`}>
                      {pkg.title}
                    </h3>
                    <p className="font-body text-sm font-medium text-[#6f5d3a]" data-testid={`package-duration-${index}`}>
                      {pkg.duration}
                    </p>
                    <p className="font-accent text-2xl font-bold italic text-[#8b6315]" data-testid={`package-price-${index}`}>
                      Starting from {pkg.priceRange.trim()}
                    </p>
                    <ul className="space-y-2" data-testid={`package-highlights-${index}`}>
                      {pkg.highlights.map((highlight, hIndex) =>
                    <li
                      key={`${pkg.title}-${highlight}`}
                      className="font-body text-sm text-[#4f4436]"
                      data-testid={`package-highlight-${index}-${hIndex}`}>

                          • {highlight}
                        </li>
                    )}
                    </ul>
                  </CardContent>
                </Card>
              </RevealBlock>
            )}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-8 lg:py-28" data-testid="live-deals-section">
        <RevealBlock className="mb-11 space-y-4" delay={60} testId="live-deals-header-wrap">
          <p className="font-body text-sm uppercase tracking-[0.24em] text-[#f2c562]" data-testid="live-deals-kicker">
            LIVE PACKAGES HIGHLIGHT
          </p>
          <h2 className="font-display text-4xl font-semibold text-[#fff8eb] sm:text-5xl" data-testid="live-deals-heading">
            Exclusive Live Deals
          </h2>
          <p className="max-w-3xl font-body text-sm text-[#e3d7bf] md:text-base" data-testid="live-deals-subtext">
            Fresh inventory drops and upcoming departures. Tap any deal to enquire instantly on WhatsApp.
          </p>
        </RevealBlock>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3" data-testid="live-deals-grid">
          {liveDealsData.map((deal, index) => (
            <RevealBlock key={deal.title} delay={120 + index * 70}>
              <a
                href={buildDealWhatsappLink(deal.title)}
                target="_blank"
                rel="noreferrer"
                className="group block overflow-hidden rounded-xl border border-[#f3c75d45] bg-[#15110a] transition-transform duration-300 hover:-translate-y-1"
                data-testid={`live-deal-card-link-${index}`}
              >
                <div className="relative aspect-[4/5] overflow-hidden" data-testid={`live-deal-image-wrap-${index}`}>
                  <img
                    src={deal.image}
                    alt={deal.title}
                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                    data-testid={`live-deal-image-${index}`}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                </div>

                <CardContent className="space-y-3 p-5" data-testid={`live-deal-content-${index}`}>
                  <h3 className="font-display text-3xl text-[#fff3dc]" data-testid={`live-deal-title-${index}`}>
                    {deal.title}
                  </h3>
                  <p className="font-body text-xs uppercase tracking-[0.14em] text-[#f1ce83]" data-testid={`live-deal-route-${index}`}>
                    {deal.route}
                  </p>
                  <p className="font-body text-sm text-[#e4d7c1]" data-testid={`live-deal-seats-${index}`}>
                    {deal.seats}
                  </p>
                  <Button
                    type="button"
                    className="gold-pill-small"
                    data-testid={`live-deal-enquire-button-${index}`}
                  >
                    Enquire on WhatsApp
                  </Button>
                </CardContent>
              </a>
            </RevealBlock>
          ))}
        </div>
      </section>

      <section id="why-us" className="mx-auto max-w-7xl px-4 py-20 sm:px-8 lg:py-28" data-testid="why-choose-us-section">
        <RevealBlock className="mb-10 space-y-4" delay={60} testId="why-us-header-wrap">
          <p className="font-body text-sm uppercase tracking-[0.24em] text-[#f2c562]" data-testid="why-us-kicker">
            Why Choose Us
          </p>
          <h2 className="font-display text-4xl font-semibold text-[#fff8eb] sm:text-5xl" data-testid="why-us-heading">
            Premium Care from Planning to Landing
          </h2>
        </RevealBlock>

        <div className="grid grid-cols-1 gap-5 md:grid-cols-2" data-testid="why-us-grid">
          {whyChooseItems.map((item, index) => {
            const Icon = featureIcons[item.title] || AirplaneTakeoff;

            return (
              <RevealBlock key={item.title} delay={110 + index * 65}>
                <Card className="glass-lux-card border border-[#f3c75d40] p-6" data-testid={`why-us-card-${index}`}>
                  <CardContent className="p-0">
                    <div className="mb-4 flex items-center justify-between">
                      <span className="font-display text-3xl text-[#f7c75f]" data-testid={`why-us-number-${index}`}>
                        0{index + 1}
                      </span>
                      <Icon size={28} className="text-[#f7c75f]" data-testid={`why-us-icon-${index}`} />
                    </div>
                    <h3 className="mb-2 font-display text-2xl text-[#fff5df]" data-testid={`why-us-title-${index}`}>
                      {item.title}
                    </h3>
                    <p className="font-body text-sm text-[#e7dbc6]" data-testid={`why-us-description-${index}`}>
                      {item.description}
                    </p>
                  </CardContent>
                </Card>
              </RevealBlock>);

          })}
        </div>
      </section>

      <section className="testimonial-dark-panel px-4 py-20 sm:px-8 lg:py-28" data-testid="testimonials-section">
        <div className="mx-auto max-w-6xl">
          <RevealBlock className="mb-11 space-y-4 text-left" delay={50} testId="testimonials-header-wrap">
            <p className="font-body text-sm uppercase tracking-[0.24em] text-[#f6c96a]" data-testid="testimonials-kicker">
              Customer Testimonials
            </p>
            <h2 className="font-display text-4xl font-semibold text-[#fff7eb] sm:text-5xl" data-testid="testimonials-heading">
              Stories from Happy Travellers
            </h2>
          </RevealBlock>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-2" data-testid="testimonials-grid">
            {testimonials.map((item, index) =>
            <RevealBlock key={item.name} delay={80 + index * 70}>
                <Card className="glass-lux-card border border-[#f3c75d3b] p-6" data-testid={`testimonial-card-${index}`}>
                  <CardContent className="space-y-4 p-0">
                    <p className="font-accent text-4xl text-[#f3c25a]" data-testid={`testimonial-quote-icon-${index}`}>
                      “
                    </p>
                    <p className="font-body text-base text-[#f2e7d4]" data-testid={`testimonial-text-${index}`}>
                      {item.text}
                    </p>
                    <p className="font-display text-xl text-[#fff9ee]" data-testid={`testimonial-name-${index}`}>
                      {item.name}
                    </p>
                  </CardContent>
                </Card>
              </RevealBlock>
            )}
          </div>
        </div>
      </section>

      <section className="mx-auto grid max-w-7xl grid-cols-1 gap-10 px-4 py-20 sm:px-8 lg:grid-cols-2 lg:py-28" data-testid="about-section">
        <RevealBlock delay={80}>
          <div className="relative aspect-[4/3] overflow-hidden rounded-sm border border-[#f4ce7542]" data-testid="about-image-wrap">
            <img src={aboutImage} alt="Personalized luxury travel" className="h-full w-full object-cover" data-testid="about-image" />
          </div>
        </RevealBlock>

        <RevealBlock className="space-y-5 self-center" delay={140}>
          <p className="font-body text-sm uppercase tracking-[0.24em] text-[#f2c562]" data-testid="about-kicker">
            About Us
          </p>
          <h2 className="font-display text-4xl font-semibold text-[#fff8eb] sm:text-5xl" data-testid="about-heading">
            We Design Holidays That Feel Uniquely Yours
          </h2>
          <p className="font-body text-base leading-relaxed text-[#e4d7c0] md:text-lg" data-testid="about-description">
            Established in 2020, My Kind Of Trip was born from the shared dream and passion for travel of our founders,
            Mr. Tanish Dutta and Ms. Tanvi Dutta, who bring over a decade of travel-industry expertise. Their vision is
            simple: make every journey memorable, every holiday unforgettable, and every experience deeply fulfilling.
            From itinerary planning to on-trip support, our team goes far beyond bookings — helping travelers discover
            authentic local cuisine, trusted guides, smart shopping spots, and smooth assistance at every step.
          </p>
          <p className="font-body text-base leading-relaxed text-[#e4d7c0] md:text-lg" data-testid="about-description-extended">
            Over the years, we have served thousands of happy customers with personalized, high-quality travel
            experiences tailored to their dreams and preferences. Because for us, travel is not just about destinations —
            it is about the people you travel with, the memories you create, and the stories you bring home.
          </p>
          <p className="font-display text-2xl italic text-[#f2c562]" data-testid="about-brand-promise">
            My Kind Of Trip — Your journey, our passion.
          </p>
        </RevealBlock>
      </section>

      <section id="visa" className="mx-auto max-w-7xl px-4 py-20 sm:px-8 lg:py-28" data-testid="visa-section">
        <RevealBlock className="mb-11 space-y-4" delay={50} testId="visa-header-wrap">
          <p className="font-body text-sm uppercase tracking-[0.24em] text-[#f2c562]" data-testid="visa-kicker">
            Visa Section
          </p>
          <h2 className="font-display text-4xl font-semibold text-[#fff8eb] sm:text-5xl" data-testid="visa-heading">
            Quick Visa Assistance for Stress-Free Travel
          </h2>
        </RevealBlock>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-3" data-testid="visa-card-grid">
          {visaAssistance.map((item, index) =>
          <RevealBlock key={item.title} delay={90 + index * 70}>
              <Card className="glass-lux-card border border-[#f3c75d40] p-6" data-testid={`visa-card-${index}`}>
                <CardContent className="p-0">
                  <h3 className="mb-3 font-display text-2xl text-[#fff5df]" data-testid={`visa-title-${index}`}>
                    {item.title}
                  </h3>
                  <p className="font-body text-sm text-[#e5d8c0]" data-testid={`visa-description-${index}`}>
                    {item.description}
                  </p>
                </CardContent>
              </Card>
            </RevealBlock>
          )}
        </div>
      </section>

      <section id="flights" className="premium-panel px-4 py-16 sm:px-8 lg:py-24" data-testid="flights-section">
        <div className="mx-auto max-w-7xl">
          <RevealBlock className="ultra-light-card rounded-xl p-8 text-left sm:p-10" delay={60} testId="flights-cta-wrap">
            <p className="font-body text-sm font-semibold uppercase tracking-[0.24em] text-[#7e5a1a]" data-testid="flights-kicker">
              Book Flights
            </p>
            <h2 className="mt-3 font-display text-4xl font-semibold text-[#17120b] sm:text-5xl" data-testid="flights-heading">
              Secure Best Routes and Smart Fare Options
            </h2>
            <p className="mt-4 max-w-3xl font-body text-base text-[#4f4436]" data-testid="flights-description">
              Tell us your destination and travel dates — our team will share optimized flight options tailored to your trip plan.
            </p>
            <Button
              type="button"
              className="gold-cta-btn mt-7"
              onClick={() => scrollToSection("contact")}
              data-testid="flights-get-deals-button">

              Get Flight Deals
            </Button>
          </RevealBlock>
        </div>
      </section>

      <section id="contact" className="premium-panel px-4 py-20 sm:px-8 lg:py-28" data-testid="contact-section">
        <div className="mx-auto max-w-7xl">
          <RevealBlock className="mb-11 space-y-4" delay={40} testId="contact-header-wrap">
            <p className="font-body text-sm font-bold uppercase tracking-[0.24em] text-[#7e5a1a]" data-testid="contact-kicker">
              Contact / Enquiry
            </p>
            <h2 className="font-display text-4xl font-semibold text-[#17120b] sm:text-5xl" data-testid="contact-heading">
              Plan Your Next International Escape
            </h2>
          </RevealBlock>

          <RevealBlock delay={120}>
            <Card className="ultra-light-card p-4 sm:p-8" data-testid="enquiry-form-card">
              <CardContent className="p-0">
                <Form {...form}>
                  <form onSubmit={form.handleSubmit(submitEnquiry)} className="grid grid-cols-1 gap-5 md:grid-cols-2" data-testid="enquiry-form">
                    <FormField
                      control={form.control}
                      name="name"
                      render={({ field }) =>
                      <FormItem data-testid="enquiry-name-field-wrap">
                          <FormLabel data-testid="enquiry-name-label">Name</FormLabel>
                          <FormControl>
                            <Input {...field} placeholder="Your full name" data-testid="enquiry-name-input" />
                          </FormControl>
                          <FormMessage data-testid="enquiry-name-error" />
                        </FormItem>
                      } />


                    <FormField
                      control={form.control}
                      name="phone"
                      render={({ field }) =>
                      <FormItem data-testid="enquiry-phone-field-wrap">
                          <FormLabel data-testid="enquiry-phone-label">Phone</FormLabel>
                          <FormControl>
                            <Input {...field} placeholder="+91 84484 18990" data-testid="enquiry-phone-input" />
                          </FormControl>
                          <FormMessage data-testid="enquiry-phone-error" />
                        </FormItem>
                      } />


                    <FormField
                      control={form.control}
                      name="email"
                      render={({ field }) =>
                      <FormItem data-testid="enquiry-email-field-wrap">
                          <FormLabel data-testid="enquiry-email-label">Email</FormLabel>
                          <FormControl>
                            <Input {...field} placeholder="you@example.com" data-testid="enquiry-email-input" />
                          </FormControl>
                          <FormMessage data-testid="enquiry-email-error" />
                        </FormItem>
                      } />


                    <FormField
                      control={form.control}
                      name="destination"
                      render={({ field }) =>
                      <FormItem data-testid="enquiry-destination-field-wrap">
                          <FormLabel data-testid="enquiry-destination-label">Destination</FormLabel>
                          <FormControl>
                            <Input {...field} placeholder="Bali / Maldives / Europe" data-testid="enquiry-destination-input" />
                          </FormControl>
                          <FormMessage data-testid="enquiry-destination-error" />
                        </FormItem>
                      } />


                    <FormField
                      control={form.control}
                      name="travel_dates"
                      render={({ field }) =>
                      <FormItem className="md:col-span-2" data-testid="enquiry-travel-dates-field-wrap">
                          <FormLabel data-testid="enquiry-travel-dates-label">Travel Dates</FormLabel>
                          <FormControl>
                            <Input {...field} placeholder="e.g. 14 Sep 2026 - 21 Sep 2026" data-testid="enquiry-travel-dates-input" />
                          </FormControl>
                          <FormMessage data-testid="enquiry-travel-dates-error" />
                        </FormItem>
                      } />


                    <Button
                      type="submit"
                      className="gold-cta-btn md:col-span-2"
                      disabled={isSubmitting}
                      data-testid="enquiry-submit-button">

                      {isSubmitting ? "Sending your enquiry..." : "Submit Enquiry"}
                    </Button>
                  </form>
                </Form>
              </CardContent>
            </Card>
          </RevealBlock>
        </div>
      </section>

      <footer className="border-t border-[#d6c39b] bg-[#e7d9bd] px-4 py-10 sm:px-8" data-testid="footer-section">
        <div className="mx-auto flex max-w-7xl flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <img src={brandLogo} alt="My Kind of Trip" className="h-20 w-auto object-contain sm:h-24" data-testid="footer-brand-logo" />
          <div className="flex flex-col gap-2 sm:items-end">
            <a
              href={instagramLink}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 text-sm font-semibold text-[#3f2f12] transition-colors duration-300 hover:text-[#8d6415]"
              data-testid="footer-instagram-link">

              <InstagramLogo size={18} weight="fill" data-testid="footer-instagram-icon" />
              Follow us on Instagram
            </a>
            <p className="font-body text-sm text-[#453316]" data-testid="footer-copy-text">
              Personalized luxury holidays for modern travellers.
            </p>
          </div>
        </div>
      </footer>

      <a
        href={whatsappLink}
        target="_blank"
        rel="noreferrer"
        className="fixed bottom-5 right-5 z-40 flex items-center gap-2 rounded-full bg-[#25D366] px-5 py-3 text-sm font-semibold text-white shadow-lg shadow-[#25D36670] transition-transform duration-300 hover:scale-[1.07]"
        data-testid="floating-whatsapp-button">

        <WhatsappLogo size={22} weight="fill" data-testid="floating-whatsapp-icon" />
        WhatsApp
      </a>

      <Toaster richColors position="top-right" />
    </div>);

};

const LoginPage = () => {
  const navigate = useNavigate();

  useEffect(() => {
    document.title = "Login | My Kind of Trip";

    const checkSession = async () => {
      try {
        await axios.get(`${API}/auth/me`, { withCredentials: true });
        navigate("/dashboard", { replace: true });
      } catch (_error) {
        // keep login page visible when not authenticated
      }
    };

    checkSession();
  }, [navigate]);

  const handleGoogleSignIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <section className="min-h-screen bg-[#0d0a06] px-4 py-24 text-[#f7ecd6] sm:px-8" data-testid="login-page">
      <div className="mx-auto grid max-w-4xl grid-cols-1 gap-8 md:grid-cols-2" data-testid="login-page-grid">
        <Card className="rounded-xl border border-[#f3c75d5e] bg-[#171108] p-7 shadow-[0_20px_44px_rgba(0,0,0,0.45)]" data-testid="login-card-main">
          <CardContent className="space-y-5 p-0">
            <p className="font-body text-xs uppercase tracking-[0.24em] text-[#f1cd82]" data-testid="login-kicker">
              Welcome Back
            </p>
            <h1 className="font-display text-4xl font-semibold text-[#fff2d8]" data-testid="login-heading">
              Continue with Google
            </h1>
            <p className="font-body text-sm text-[#dbcdb1]" data-testid="login-subtext">
              Sign in securely to view your personalized dashboard and your enquiry history.
            </p>

            <Button type="button" className="gold-cta-btn w-full" onClick={handleGoogleSignIn} data-testid="google-signin-button">
              Sign in with Google
            </Button>

            <Link to="/" className="inline-block font-body text-sm font-semibold text-[#f1cd82] underline" data-testid="login-back-home-link">
              Back to Home
            </Link>
          </CardContent>
        </Card>

        <Card className="rounded-xl border border-[#f3c75d5e] bg-[#151008] p-7 shadow-[0_20px_44px_rgba(0,0,0,0.45)]" data-testid="login-info-card">
          <CardContent className="space-y-4 p-0">
            <h2 className="font-display text-3xl text-[#fff2d8]" data-testid="login-benefits-heading">Why sign in?</h2>
            <ul className="space-y-3" data-testid="login-benefits-list">
              <li className="font-body text-sm text-[#dbcdb1]" data-testid="login-benefit-item-0">• Track your submitted enquiries in one place</li>
              <li className="font-body text-sm text-[#dbcdb1]" data-testid="login-benefit-item-1">• Get a personalized welcome dashboard</li>
              <li className="font-body text-sm text-[#dbcdb1]" data-testid="login-benefit-item-2">• Access your journey details faster</li>
            </ul>
          </CardContent>
        </Card>
      </div>
    </section>
  );
};

const AuthCallback = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hashParams = new URLSearchParams(location.hash.replace("#", ""));
    const sessionId = hashParams.get("session_id");

    if (!sessionId) {
      navigate("/login", { replace: true });
      return;
    }

    const processSession = async () => {
      try {
        const response = await axios.post(`${API}/auth/session`, { session_id: sessionId }, { withCredentials: true });
        window.history.replaceState({}, document.title, window.location.pathname + window.location.search);
        navigate("/dashboard", { replace: true, state: { user: response.data.user } });
      } catch (error) {
        toast.error(error?.response?.data?.detail || "Google login failed. Please try again.");
        navigate("/login", { replace: true });
      }
    };

    processSession();
  }, [location.hash, navigate]);

  return (
    <section className="premium-panel min-h-screen px-4 py-24 sm:px-8" data-testid="auth-callback-page">
      <div className="mx-auto max-w-xl">
        <Card className="ultra-light-card p-8" data-testid="auth-callback-card">
          <CardContent className="space-y-4 p-0 text-center">
            <h2 className="font-display text-3xl text-[#17120b]" data-testid="auth-callback-heading">Signing you in...</h2>
            <p className="font-body text-sm text-[#4f4436]" data-testid="auth-callback-text">Please wait while we complete your secure Google login.</p>
          </CardContent>
        </Card>
      </div>
    </section>
  );
};

const ProtectedRoute = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const location = useLocation();

  useEffect(() => {
    if (location.state?.user) {
      setIsAuthenticated(true);
      return;
    }

    const checkAuth = async () => {
      try {
        await axios.get(`${API}/auth/me`, { withCredentials: true });
        setIsAuthenticated(true);
      } catch (_error) {
        setIsAuthenticated(false);
      }
    };

    checkAuth();
  }, [location.state]);

  if (isAuthenticated === null) {
    return (
      <section className="premium-panel min-h-screen px-4 py-24 sm:px-8" data-testid="auth-loading-page">
        <div className="mx-auto max-w-xl">
          <Card className="ultra-light-card p-8" data-testid="auth-loading-card">
            <CardContent className="p-0 text-center">
              <p className="font-body text-sm text-[#4f4436]" data-testid="auth-loading-text">Checking your session...</p>
            </CardContent>
          </Card>
        </div>
      </section>
    );
  }

  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return children;
};

const DashboardPage = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);

  const groupedEnquiries = useMemo(() => {
    const groups = {};
    enquiries.forEach((enquiry) => {
      const date = new Date(enquiry.created_at);
      const monthLabel = date.toLocaleString("en-US", { month: "long", year: "numeric" });
      if (!groups[monthLabel]) groups[monthLabel] = [];
      groups[monthLabel].push(enquiry);
    });
    return Object.entries(groups);
  }, [enquiries]);

  useEffect(() => {
    document.title = "Dashboard | My Kind of Trip";

    const loadDashboard = async () => {
      try {
        const [meResponse, enquiriesResponse] = await Promise.all([
          axios.get(`${API}/auth/me`, { withCredentials: true }),
          axios.get(`${API}/auth/my-enquiries`, { withCredentials: true }),
        ]);

        setUser(meResponse.data);
        setEnquiries(enquiriesResponse.data || []);
      } catch (_error) {
        navigate("/login", { replace: true });
      } finally {
        setLoading(false);
      }
    };

    loadDashboard();
  }, [navigate]);

  const handleLogout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    navigate("/login", { replace: true });
  };

  if (loading) {
    return (
      <section className="min-h-screen bg-[#0d0a06] px-4 py-24 text-[#f7ecd6] sm:px-8" data-testid="dashboard-loading-page">
        <div className="mx-auto max-w-xl">
          <Card className="rounded-xl border border-[#f3c75d5e] bg-[#151008] p-8" data-testid="dashboard-loading-card">
            <CardContent className="p-0 text-center">
              <p className="font-body text-sm text-[#dbcdb1]" data-testid="dashboard-loading-text">Loading your dashboard...</p>
            </CardContent>
          </Card>
        </div>
      </section>
    );
  }

  return (
    <section className="min-h-screen bg-[#0d0a06] px-4 py-16 text-[#f7ecd6] sm:px-8" data-testid="dashboard-page">
      <div className="mx-auto max-w-7xl space-y-8" data-testid="dashboard-content-wrap">
        <Card className="rounded-xl border border-[#f3c75d5e] bg-[#151008] p-7 shadow-[0_20px_44px_rgba(0,0,0,0.45)]" data-testid="dashboard-welcome-card">
          <CardContent className="flex flex-col gap-6 p-0 md:flex-row md:items-start md:justify-between">
            <div className="space-y-2" data-testid="dashboard-user-summary">
              <p className="font-body text-xs uppercase tracking-[0.24em] text-[#f1cd82]" data-testid="dashboard-kicker">Welcome</p>
              <h1 className="font-display text-4xl text-[#fff2d8]" data-testid="dashboard-user-name">{user?.name}</h1>
              <p className="font-body text-sm text-[#dbcdb1]" data-testid="dashboard-user-email">{user?.email}</p>
              <p className="font-body text-sm text-[#dbcdb1]" data-testid="dashboard-welcome-note">Your personalized trip support dashboard is ready.</p>
            </div>

            <div className="space-y-3" data-testid="dashboard-actions-wrap">
              <div className="grid grid-cols-2 gap-3" data-testid="dashboard-stats-grid">
                <div className="rounded-lg border border-[#f3c75d45] bg-[#0f0b06] p-3" data-testid="dashboard-stat-total-enquiries">
                  <p className="font-body text-[10px] uppercase tracking-[0.18em] text-[#f1cd82]">Total Enquiries</p>
                  <p className="font-display text-2xl text-[#fff2d8]">{enquiries.length}</p>
                </div>
                <div className="rounded-lg border border-[#f3c75d45] bg-[#0f0b06] p-3" data-testid="dashboard-stat-last-update">
                  <p className="font-body text-[10px] uppercase tracking-[0.18em] text-[#f1cd82]">Last Update</p>
                  <p className="font-body text-sm text-[#fff2d8]">{enquiries[0] ? new Date(enquiries[0].created_at).toLocaleDateString() : "—"}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
              <Link to="/" className="gold-cta-outline-btn" data-testid="dashboard-home-link">Back to Home</Link>
              <Button type="button" className="gold-cta-btn" onClick={handleLogout} data-testid="dashboard-logout-button">Logout</Button>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-xl border border-[#f3c75d5e] bg-[#151008] p-7 shadow-[0_20px_44px_rgba(0,0,0,0.45)]" data-testid="dashboard-enquiries-card">
          <CardContent className="space-y-5 p-0">
            <h2 className="font-display text-3xl text-[#fff2d8]" data-testid="dashboard-enquiries-heading">My Enquiries</h2>

            {enquiries.length === 0 ? (
              <p className="font-body text-sm text-[#dbcdb1]" data-testid="dashboard-enquiries-empty">No enquiries found yet for your email.</p>
            ) : (
              <div className="space-y-7" data-testid="dashboard-enquiries-grouped-wrap">
                {groupedEnquiries.map(([monthLabel, monthEnquiries], groupIndex) => (
                  <div key={monthLabel} className="space-y-3" data-testid={`dashboard-enquiry-group-${groupIndex}`}>
                    <p className="font-body text-xs uppercase tracking-[0.2em] text-[#f1cd82]" data-testid={`dashboard-enquiry-group-title-${groupIndex}`}>
                      {monthLabel}
                    </p>
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2" data-testid={`dashboard-enquiry-group-grid-${groupIndex}`}>
                      {monthEnquiries.map((enquiry, index) => (
                        <div
                          key={enquiry.id}
                          className="rounded-lg border border-[#f3c75d45] bg-[#0f0b06] p-4"
                          data-testid={`dashboard-enquiry-card-${groupIndex}-${index}`}
                        >
                          <span className="inline-flex rounded-full border border-[#f3c75d66] bg-[#1b1409] px-3 py-1 font-body text-[10px] uppercase tracking-[0.16em] text-[#f7d98d]" data-testid={`dashboard-enquiry-badge-${groupIndex}-${index}`}>
                            Destination
                          </span>
                          <p className="mt-2 font-display text-2xl text-[#fff2d8]" data-testid={`dashboard-enquiry-destination-${groupIndex}-${index}`}>{enquiry.destination}</p>
                          <p className="font-body text-sm text-[#dbcdb1]" data-testid={`dashboard-enquiry-dates-${groupIndex}-${index}`}>{enquiry.travel_dates}</p>
                          <p className="mt-1 font-body text-xs uppercase tracking-[0.14em] text-[#b79a64]" data-testid={`dashboard-enquiry-created-${groupIndex}-${index}`}>
                            Submitted {new Date(enquiry.created_at).toLocaleDateString()}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </section>
  );
};

const AppRouter = () => {
  const location = useLocation();

  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/login" element={<LoginPage />} />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <div className="App" data-testid="app-root">
      <BrowserRouter>
        <AppRouter />
      </BrowserRouter>
    </div>);

}

export default App;